import React from 'react';
import './LegalPages.css';

const Terms: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: January 23, 2026</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using Cruise Spotter Connect's services, you agree to be bound by these
            Terms of Service. If you do not agree, do not use our services.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            Cruise Spotter Connect, LLC provides a B2B lead generation and call routing platform. We:
          </p>
          <ul>
            <li><strong>ARE:</strong> A marketing and lead generation service</li>
            <li><strong>ARE:</strong> A call routing platform connecting consumers with travel agents</li>
            <li><strong>ARE:</strong> A marketing services provider</li>
          </ul>
          <p><strong>We ARE NOT:</strong></p>
          <ul>
            <li>A travel agency</li>
            <li>A cruise booking platform</li>
            <li>A seller of travel</li>
            <li>An employer of travel agents (agents are independent contractors)</li>
            <li>Responsible for travel bookings, pricing, or availability</li>
          </ul>
        </section>

        <section>
          <h2>3. Eligibility</h2>
          <p>To use our services, you must:</p>
          <ul>
            <li>Be at least 18 years old</li>
            <li>Be a licensed travel advisor or working under a licensed agency</li>
            <li>Have authority to enter into this agreement</li>
            <li>Comply with all applicable laws and regulations</li>
            <li>Maintain appropriate travel advisor licenses and insurance</li>
          </ul>
        </section>

        <section>
          <h2>4. Agent Responsibilities</h2>
          <p>As an independent travel advisor using our platform, you are responsible for:</p>
          <ul>
            <li>Maintaining all required licenses and certifications</li>
            <li>Complying with all travel industry regulations</li>
            <li>Providing accurate information to customers</li>
            <li>Handling all bookings, payments, and customer service directly</li>
            <li>Maintaining appropriate errors & omissions insurance</li>
            <li>Filing your own taxes (you are an independent contractor, not our employee)</li>
            <li>Following up with customers you're connected with</li>
            <li>Providing professional and courteous service</li>
          </ul>
        </section>

        <section>
          <h2>5. What We Provide</h2>
          <p>We provide:</p>
          <ul>
            <li>Marketing to generate cruise-interested leads</li>
            <li>Real-time call routing to available agents</li>
            <li>Call recording for quality assurance and dispute resolution</li>
            <li>Billing and payment processing via Stripe</li>
            <li>Dashboard for tracking calls and billing</li>
            <li>Technical support for platform issues</li>
          </ul>
        </section>

        <section>
          <h2>6. What We Don't Provide</h2>
          <p>We do not:</p>
          <ul>
            <li>Guarantee any specific number of calls or leads</li>
            <li>Guarantee conversion rates or booking success</li>
            <li>Provide travel booking services</li>
            <li>Guarantee pricing or availability of cruises</li>
            <li>Handle customer payments for travel bookings</li>
            <li>Provide legal or tax advice</li>
            <li>Guarantee income or earnings</li>
            <li>Employ or supervise travel agents</li>
          </ul>
        </section>

        <section>
          <h2>7. Pricing and Billing</h2>
          <h3>Call Pricing</h3>
          <ul>
            <li>Standard Calls (social media traffic): $20 per qualified call</li>
            <li>Premium Calls (email list traffic): $40 per qualified call</li>
          </ul>

          <h3>Billable Call Criteria</h3>
          <p>A call is billable only if ALL of the following criteria are met:</p>
          <ul>
            <li>Call duration is 90 seconds or longer</li>
            <li>Call involves a cruise-related inquiry</li>
            <li>Caller is a legitimate customer (not spam or robocall)</li>
            <li>It is the first call from that phone number</li>
            <li>Call was successfully connected and conversation occurred</li>
          </ul>

          <h3>Non-Billable Calls</h3>
          <p>You are NOT charged for:</p>
          <ul>
            <li>Calls under 90 seconds</li>
            <li>Non-cruise related inquiries</li>
            <li>Spam, robocalls, or obviously fraudulent calls</li>
            <li>Duplicate calls from the same number within 24 hours</li>
            <li>Technical issues preventing conversation</li>
            <li>Follow-up calls from customers already assigned to you</li>
          </ul>

          <h3>Billing Schedule</h3>
          <ul>
            <li>Billing occurs monthly on the 1st of each month</li>
            <li>Charges are for calls received in the previous month</li>
            <li>Payment is automatic via credit/debit card through Stripe</li>
            <li>Detailed invoices provided with each billing</li>
          </ul>
        </section>

        <section>
          <h2>8. Payment Terms</h2>
          <ul>
            <li>You authorize automatic monthly charges to your payment method</li>
            <li>You must maintain a valid payment method on file</li>
            <li>Failed payments may result in account suspension</li>
            <li>You remain responsible for unpaid charges even after account termination</li>
            <li>All fees are in USD</li>
            <li>We do not offer refunds except as described in our Refund & Dispute Policy</li>
          </ul>
        </section>

        <section>
          <h2>9. Account Management</h2>
          <h3>Your Account</h3>
          <ul>
            <li>You are responsible for maintaining account security</li>
            <li>Do not share your login credentials</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>You are responsible for all activity under your account</li>
          </ul>

          <h3>Availability Toggle</h3>
          <ul>
            <li>You can toggle your availability on/off at any time</li>
            <li>When "Unavailable," you will not receive calls or charges</li>
            <li>You control your spend by managing your availability</li>
          </ul>
        </section>

        <section>
          <h2>10. Call Recordings</h2>
          <p>
            All calls are recorded for quality assurance, training, and dispute resolution. By using
            our service, you consent to call recording. Recorded calls:
          </p>
          <ul>
            <li>Are stored securely for 90 days</li>
            <li>May be reviewed for billing disputes</li>
            <li>May be used for quality assurance and training</li>
            <li>Will not be shared publicly or sold to third parties</li>
            <li>Are automatically deleted after 90 days</li>
          </ul>
        </section>

        <section>
          <h2>11. Termination</h2>
          <h3>Termination by You</h3>
          <ul>
            <li>You may cancel your account at any time by contacting support</li>
            <li>No cancelation fees</li>
            <li>You remain responsible for charges incurred before cancelation</li>
            <li>To pause service without canceling, toggle availability to "Unavailable"</li>
          </ul>

          <h3>Termination by Us</h3>
          <p>We may suspend or terminate your account if you:</p>
          <ul>
            <li>Violate these Terms of Service</li>
            <li>Fail to maintain required licenses</li>
            <li>Engage in fraudulent activity</li>
            <li>Have multiple failed payments</li>
            <li>Provide poor service to customers (repeated complaints)</li>
            <li>Abuse the dispute process</li>
          </ul>
        </section>

        <section>
          <h2>12. Independent Contractor Relationship</h2>
          <p>
            You are an independent contractor, not an employee of Cruise Spotter Connect. This means:
          </p>
          <ul>
            <li>You are not entitled to employment benefits</li>
            <li>You are responsible for your own taxes</li>
            <li>You set your own work schedule and methods</li>
            <li>We do not supervise or control how you conduct your business</li>
            <li>You are free to work with other lead sources or platforms</li>
            <li>We will issue Form 1099 if you earn over $600 per year</li>
          </ul>
        </section>

        <section>
          <h2>13. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, CRUISE SPOTTER CONNECT SHALL NOT BE LIABLE FOR:
          </p>
          <ul>
            <li>Any indirect, incidental, consequential, or punitive damages</li>
            <li>Lost profits or revenue</li>
            <li>Loss of business or opportunities</li>
            <li>Errors or omissions by travel agents</li>
            <li>Customer disputes with travel agents</li>
            <li>Travel booking issues, cancellations, or changes</li>
            <li>Third-party service failures (Stripe, Aircall, etc.)</li>
          </ul>
          <p>
            Our total liability for any claim shall not exceed the amount you paid us in the 3 months
            prior to the claim.
          </p>
        </section>

        <section>
          <h2>14. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Cruise Spotter Connect from any claims, damages,
            or expenses arising from:
          </p>
          <ul>
            <li>Your use of our services</li>
            <li>Your violation of these terms</li>
            <li>Your violation of laws or regulations</li>
            <li>Your interactions with customers</li>
            <li>Travel bookings you facilitate</li>
            <li>Your lack of required licenses or insurance</li>
          </ul>
        </section>

        <section>
          <h2>15. Disclaimer of Warranties</h2>
          <p>
            OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            WE DISCLAIM ALL WARRANTIES INCLUDING BUT NOT LIMITED TO:
          </p>
          <ul>
            <li>Warranties of merchantability or fitness for a particular purpose</li>
            <li>Warranties regarding uptime or availability</li>
            <li>Warranties regarding lead quality or conversion rates</li>
            <li>Warranties regarding income or earnings potential</li>
          </ul>
        </section>

        <section>
          <h2>16. Dispute Resolution</h2>
          <h3>Billing Disputes</h3>
          <p>
            For billing disputes, see our{' '}
            <a href="/refund-policy">Refund & Dispute Policy</a>. You must notify us within 15 days
            of the invoice date.
          </p>

          <h3>Legal Disputes</h3>
          <p>
            Any disputes arising from these terms shall be resolved through binding arbitration in
            accordance with the rules of the American Arbitration Association. You waive any right
            to a jury trial or class action.
          </p>

          <h3>Governing Law</h3>
          <p>
            These terms are governed by the laws of the state where Cruise Spotter Connect, LLC is
            registered, without regard to conflict of law provisions.
          </p>
        </section>

        <section>
          <h2>17. Changes to Terms</h2>
          <p>
            We may modify these terms at any time. We will notify you of material changes via email
            or platform notification. Continued use after changes constitutes acceptance. If you don't
            agree to changes, you may terminate your account.
          </p>
        </section>

        <section>
          <h2>18. Miscellaneous</h2>
          <h3>Entire Agreement</h3>
          <p>
            These terms constitute the entire agreement between you and Cruise Spotter Connect
            regarding our services.
          </p>

          <h3>Severability</h3>
          <p>
            If any provision is found unenforceable, the remaining provisions remain in effect.
          </p>

          <h3>No Waiver</h3>
          <p>
            Our failure to enforce any right or provision does not constitute a waiver of that right.
          </p>

          <h3>Assignment</h3>
          <p>
            You may not assign or transfer these terms. We may assign our rights to any affiliate or
            successor.
          </p>
        </section>

        <section>
          <h2>19. Contact Information</h2>
          <p>For questions about these Terms of Service:</p>
          <div className="contact-info">
            <p><strong>Cruise Spotter Connect, LLC</strong></p>
            <p>Email: <a href="mailto:hello@cruisespotterconnect.com">hello@cruisespotterconnect.com</a></p>
            <p>Hours: Monday-Friday, 9 AM - 6 PM EST</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Terms;
